import './styles.scss';

import { ComponentProps, ReactElement, useMemo } from 'react';
import { InvoiceCustomCharge, RunPreviewInvoiceResponse } from '@/models/gen/graphql';

import CustomPdfTableHeader from '@/features/PdfLayout/layouts/Invoice/CustomPdfTable/CustomPdfTableHeader';
import CustomPdfTableRow from '@/features/PdfLayout/layouts/Invoice/CustomPdfTable/CustomPdfTableRow';
import Table from '@/common/Table';
import { getClasses } from '@/utils';
import { usePdfLayout } from '@/features/PdfLayout';

export type CustomPdfTableProps = ComponentProps<typeof Table>;

const CustomPdfTable = ({ ...props }: CustomPdfTableProps): ReactElement => {
  const { customCharges, error } = usePdfLayout(({ state }: { state: { data: RunPreviewInvoiceResponse; error?: unknown } }) => ({
    customCharges: state?.data?.output?.customCharges || [],
    error: state?.error,
  }));

  const charges = useMemo(() => {
    if (!customCharges || customCharges.length === 0) return [];

    // Assign global index to each charge
    const chargesWithGlobalIndex = customCharges.map((charge, idx) => ({
      ...charge,
      index: idx + 1,
    }));

    // Group charges by type
    const grouped = chargesWithGlobalIndex.reduce<Record<string, typeof chargesWithGlobalIndex>>((acc, charge) => {
      if (!acc[charge.type]) acc[charge.type] = [];
      acc[charge.type].push(charge);
      return acc;
    }, {});

    const result: (InvoiceCustomCharge | { index: number; subtotal: number })[] = [];

    Object.values(grouped).forEach((charges) => {
      charges.forEach((charge) => result.push(charge));
      const subtotal = charges.reduce((sum, charge) => sum + (charge.cost || 0), 0);
      // Use the index of the last charge in this group for the subtotal row
      const lastChargeIndex = charges.length > 0 ? charges[charges.length - 1].index : 0;
      result.push({
        index: lastChargeIndex,
        subtotal,
      });
    });

    return result;
  }, [customCharges]);

  return (
    <Table {...props} className={getClasses('CustomPdfTable', props.className)} header={<CustomPdfTableHeader />}>
      {charges.map(
        ({ id, index: chargeIndex, ...charge }: { count: number; subtotal: number } & InvoiceCustomCharge, index: number): ReactElement => (
          <CustomPdfTableRow
            key={id || index}
            {...charge}
            index={chargeIndex}
            className={!error && index === charges.length - 1 ? 'PdfReady' : undefined}
          />
        )
      )}
    </Table>
  );
};

export default CustomPdfTable;
